// The DFT module's Quantum ESPRESSO calculation (running it is Advanced plan): choose a recipe (ready-made, from a
// structure file or a structure database, a recipe file, or pw.x inputs made with another tool), adjust it (main
// settings, the pseudopotential table prefilled from the server's library, or the whole recipe as YAML), see the
// server's checks, size and inputs, and run it. The result is
// shown with SKFitter's band and lattice views (``views``), and can go on to SKFitter.
import { Accordion, Alert, Badge, Button, Card, Checkbox, Code, FileButton, Grid, Group, List, NumberInput, ScrollArea,
  SegmentedControl, Select, SimpleGrid, Stack, Table, Tabs, Text, TextInput, Textarea, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ErrorBox, notifyError, RequestAccessModal } from "../../components/Errors";
import { CancelButton, JobProgress } from "../../components/Jobs";
import { download, post, type Job, type ProjectDetail } from "../../lib/api";
import { cutoffsFor, fmtEstimate, getIn, groupIssues, pathLabels, pathPoints, qeCheck, qeImport, qeParse, qePseudos, qeTemplate,
  recipeProtocol, roughTime, setIn, setPseudo, withDos, SMEARINGS, speciesRows, SPIN_MODES, uniqueElements, useQeStatus, type DftMemory, type PseudoOption,
  type QeIssue, type QeCheck, type QeSummary, type Recipe } from "../../lib/qe";

import { HubbardPanel, MagnetismPanel, PathEditor } from "./PhysicsTools";
import { DefectsPanel } from "./DefectTools";
import { BudgetLine } from "./RunResults";
import { ProtocolBar, UserPseudos } from "./Pseudopotentials";
import { AtomsTable, RecipeStructure, StructureImport } from "./StructureTools";
type Source = "template" | "structure" | "database" | "file" | "pwx";

async function readText(f: File): Promise<string> {
  return await f.text();
}

export const QE_KIND = "dft.qe";
/** Jobs whose result is a DFT run: a Quantum ESPRESSO run, or an illustrative project's finished run copied in. */
export const RUN_KINDS = [QE_KIND, "dft.example"];

export function QeCalculation({ project, mem, update, remember, job, views }: {
  project: ProjectDetail; mem: DftMemory; update: (m: Partial<DftMemory>) => void; remember: (m: Partial<DftMemory>) => void;
  job: Job | undefined; views: ReactNode;
}) {
  const qc = useQueryClient();
  const status = useQeStatus();
  const [recipe, setRecipe] = useState<Recipe | null>(mem.qe?.recipe ?? null);
  const [template, setTemplate] = useState<string | null>(mem.qe?.template ?? null);
  const [pristine, setPristine] = useState<boolean>(!!mem.qe?.template && !mem.qe?.changed);   // the ready-made recipe as it is
  const [source, setSource] = useState<Source>(mem.structure_file?.source ? "database" : mem.structure_file ? "structure" : mem.qe && !mem.qe.template ? "file" : "template");
  const [importIssues, setImportIssues] = useState<QeIssue[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [askAccess, setAskAccess] = useState(false);

  const change = (r: Recipe) => { setRecipe(r); setPristine(false); };
  const load = (r: Recipe, tpl: string | null, issues: QeIssue[] = [], structureFile?: DftMemory["structure_file"]) => {
    setRecipe(r); setTemplate(tpl); setPristine(!!tpl); setImportIssues(issues); setLoadError(null);
    // a recipe taken from a source is kept in the project at once (edits are kept when it runs)
    remember({ qe: { recipe: r, template: tpl }, structure_file: structureFile });
  };

  const pickTemplate = useMutation({ mutationFn: (id: string) => qeTemplate(id), onSuccess: (t) => load(t.recipe, t.id),
    onError: setLoadError });
  const fromFile = async (f: File | null) => {
    if (!f) return;
    try {
      const text = await readText(f);
      const r = await qeParse(text, /\.json$/i.test(f.name) ? "json" : undefined);
      load(r.recipe, null);
    } catch (e) { setLoadError(e); }
  };

  // -- the server's checks (debounced): issues, size, the inputs, the recipe as YAML ---------------------------------
  const [debounced] = useDebouncedValue(recipe ? JSON.stringify(recipe) : "", 400);
  const check = useQuery({
    queryKey: ["qe-check", debounced, project.id],
    queryFn: () => qeCheck(JSON.parse(debounced), project.id),
    enabled: !!debounced,
    placeholderData: (p) => p,
  });

  const busy = !!job && RUN_KINDS.includes(job.kind) && (job.status === "queued" || job.status === "running");
  const run = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: QE_KIND, project_id: project.id,
      params: pristine && template ? { template, seed: mem.seed } : { recipe, seed: mem.seed } }),
    onSuccess: (j) => {
      remember({ job: j.id, qe: { recipe: recipe!, template, changed: !pristine } });
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["qe-status"] });
    },
  });

  // the queue counts on this page follow the user's own QE job
  useEffect(() => { qc.invalidateQueries({ queryKey: ["qe-status"] }); }, [job?.status, qc]);
  const st = status.data;
  const qeResult = job && RUN_KINDS.includes(job.kind) && job.status === "succeeded" ? job.result : null;
  const noHours = !!st?.budget && st.budget.left <= 0;
  const canRun = !!st?.available && !!st?.allowed && !!check.data?.ok && !!recipe && !!mem.seed.trim() && !busy
    && !check.isFetching && !noHours;

  return (
    <Stack gap="md" mt="md">
      {st && !st.available && (
        <Alert color="gray" title="Not available on this server">Quantum ESPRESSO is not installed on this server yet. You can still
          prepare and check a recipe here.</Alert>
      )}
      {st && st.library.files === 0 && (
        <Alert color="orange" title="No pseudopotentials on this server yet" data-testid="qe-no-library">
          The pseudopotential library of this server is empty, so no recipe can run or be fully checked. An administrator
          adds the UPF files, e.g. with <Code>python3 tools/qe_pseudo/collect_pseudos.py --download</Code> (or
          <Code> --from</Code> folders that hold them) - see docs/QE_SERVER.md, "Pseudopotentials".
        </Alert>
      )}
      {st && st.available && !st.allowed && (
        <Alert color="orange" title="Advanced plan">
          <Stack gap="xs">
            <Text size="sm">Running Quantum ESPRESSO is part of the Advanced plan. You can prepare and check a recipe here;
              the checks use your current plan's limits.</Text>
            <Group><Button size="xs" variant="light" onClick={() => setAskAccess(true)}>Request access</Button></Group>
          </Stack>
        </Alert>
      )}
      <RequestAccessModal limitKey="dft.qe_run" opened={askAccess} onClose={() => setAskAccess(false)} />

      <Card withBorder>
        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <Stack gap="sm">
              <Text fw={600}>1. The recipe</Text>
              <SegmentedControl size="xs" value={source} onChange={(v) => setSource(v as Source)} data={[
                { value: "template", label: "Ready-made" }, { value: "structure", label: "Structure file" },
                { value: "database", label: "Database" }, { value: "file", label: "Recipe file" }, { value: "pwx", label: "pw.x inputs" }]} />
              {source === "template" && (
                <Select label="Ready-made recipe" searchable
                  placeholder={template && !pristine ? `${st?.templates.find((t) => t.id === template)?.name ?? template} (changed)` : "Choose a material"}
                  data={(st?.templates ?? []).map((t) => ({ value: t.id, label: t.name }))}
                  value={template && pristine ? template : null} onChange={(v) => v && pickTemplate.mutate(v)}
                  description={st?.templates.find((t) => t.id === template)?.description} />
              )}
              {source === "file" && (
                <Stack gap={4}>
                  <Text size="xs" c="dimmed">A recipe file (.yaml or .json, see the guide "Quantum ESPRESSO recipes"). Download
                    a ready-made one from the Recipe tab to start from it.</Text>
                  <FileButton onChange={fromFile} accept=".yaml,.yml,.json">
                    {(props) => <Button size="xs" variant="default" {...props}>Open a recipe file…</Button>}
                  </FileButton>
                </Stack>
              )}
              {source === "pwx" && <PwxImport onLoad={(r, issues) => load(r, null, issues)} onError={setLoadError} />}
              {(source === "structure" || source === "database") && (
                <StructureImport key={source} mode={source === "database" ? "database" : "file"} projectId={project.id}
                  protocol={recipeProtocol(recipe)}
                  saved={(source === "database") === !!mem.structure_file?.source ? mem.structure_file : undefined}
                  onLoad={(r, f) => load(r, null, [], f)} onError={setLoadError} />
              )}
              <ErrorBox error={loadError ?? pickTemplate.error} />
              {importIssues.filter((i) => i.level !== "note").length > 0 && (
                <Alert color="yellow" p="xs" title="Imported - please check">
                  <List size="xs">{importIssues.filter((i) => i.level !== "note").map((i, k) => <List.Item key={k}>{i.message}</List.Item>)}</List>
                </Alert>
              )}
              {recipe && <Settings recipe={recipe} onChange={change} />}
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 7 }}>
            {recipe ? (
              <Stack gap="sm">
                <Text fw={600}>2. Pseudopotentials</Text>
                <ProtocolBar recipe={recipe} status={st} onChange={change} />
                <PseudoTable recipe={recipe} onChange={change} projectId={project.id} />
                <UserPseudos projectId={project.id} />
                <CheckTabs recipe={recipe} check={check} nprocs={st?.nprocs ?? 1} onYaml={change} projectId={project.id} />
              </Stack>
            ) : (
              <Text c="dimmed" mt="xl">Choose a ready-made recipe, open a recipe file, or import pw.x inputs to start.</Text>
            )}
          </Grid.Col>
        </Grid>
      </Card>

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="sm">
            <Text fw={600}>3. Run</Text>
            <TextInput label="Seedname" description="The result files are called <seedname>_qe_bands.xml, …"
              value={mem.seed} onChange={(e) => update({ seed: e.currentTarget.value })} />
            <ErrorBox error={run.error} />
            <Tooltip disabled={canRun || busy} label={!st?.allowed ? "Part of the Advanced plan" : noHours ? "No Quantum ESPRESSO hours left this month"
              : !check.data?.ok ? "Fix the errors of the recipe first" : ""}>
              <Button onClick={() => run.mutate()} loading={run.isPending} disabled={!canRun} data-testid="qe-run">
                {qeResult ? "Run Quantum ESPRESSO again" : "Run Quantum ESPRESSO"}
              </Button>
            </Tooltip>
            {st?.allowed && <BudgetLine budget={st.budget} />}
            {st && st.queue.queued + st.queue.running > 0 && !busy && (
              <Text size="xs" c="dimmed">Quantum ESPRESSO runs one calculation at a time: {st.queue.running} running,
                {" "}{st.queue.queued} waiting.</Text>
            )}
            {job && busy && (
              <Stack gap={4}>
                <JobProgress job={job} />
                <Group justify="flex-end"><CancelButton job={job} /></Group>
              </Stack>
            )}
            {job && RUN_KINDS.includes(job.kind) && job.error && <ErrorBox error={new Error(job.error)} />}
            {qeResult && <QeSummaryCard summary={qeResult.qe as QeSummary} files={qeResult.qe_files as Record<string, string>}
              seed={String(qeResult.seed ?? mem.seed)} />}
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 8 }}>
          {views ?? <Text c="dimmed" mt="md">The band structure and the lattice appear here once Quantum ESPRESSO has run.</Text>}
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

/** pw.x inputs made with another tool: the SCF input, and the bands input for the k-path. */
function PwxImport({ onLoad, onError }: { onLoad: (r: Recipe, issues: QeIssue[]) => void; onError: (e: unknown) => void }) {
  const [scf, setScf] = useState<File | null>(null);
  const [bands, setBands] = useState<File | null>(null);
  const m = useMutation({
    mutationFn: async () => qeImport(await readText(scf!), bands ? await readText(bands) : null, scf!.name.replace(/\.[^.]*$/, "")),
    onSuccess: (r) => onLoad(r.recipe, r.issues), onError,
  });
  return (
    <Stack gap={6}>
      <Text size="xs" c="dimmed">Inputs written by Materials Cloud's Quantum ESPRESSO input generator, BURAI, PWgui, ASE, … The
        SCF input gives the material and the settings, the bands input the k-path. The bands and projection steps are then made here.</Text>
      <Group gap="xs">
        <FileButton onChange={setScf}>{(p) => <Button size="xs" variant="default" {...p}>SCF input…</Button>}</FileButton>
        <Text size="xs">{scf?.name ?? "none"}</Text>
      </Group>
      <Group gap="xs">
        <FileButton onChange={setBands}>{(p) => <Button size="xs" variant="default" {...p}>Bands input (optional)…</Button>}</FileButton>
        <Text size="xs">{bands?.name ?? "none"}</Text>
      </Group>
      <Group><Button size="xs" disabled={!scf} loading={m.isPending} onClick={() => m.mutate()}>Import</Button></Group>
    </Stack>
  );
}

const num = (v: unknown) => (typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v.replace(/d/i, "e"))) ? Number(v.replace(/d/i, "e")) : undefined);

/** The main settings of a recipe as a form (everything else: the YAML tab). */
function Settings({ recipe, onChange }: { recipe: Recipe; onChange: (r: Recipe) => void }) {
  const set = (path: (string | number)[], v: unknown) => onChange(setIn(recipe, path, v));
  const occ = recipe.occupations ?? {};
  const kind = typeof occ === "string" ? occ : occ.kind ?? "smearing";
  const kmesh: number[] | undefined = getIn(recipe, ["scf", "kmesh"]);
  const nbnd = getIn(recipe, ["bands", "nbnd"]);
  const lat = recipe.structure?.lattice ?? {};
  const rows = speciesRows(recipe);
  return (
    <Stack gap="xs" data-testid="qe-settings">
      <TextInput size="xs" label="Name" value={recipe.name ?? ""} onChange={(e) => set(["name"], e.currentTarget.value)} />
      <Text size="xs" c="dimmed">
        Structure: {Array.isArray(lat.vectors) ? `lattice vectors of ${lat.vectors.map((v: number[]) => Math.hypot(...v).toFixed(3)).join(", ")} Å`
          : <>ibrav {String(lat.ibrav ?? "?")}{lat.a ? `, a = ${lat.a} Å` : lat.celldm?.[1] ? `, celldm(1) = ${lat.celldm[1]} bohr` : ""}
            {lat.c ? `, c = ${lat.c} Å` : ""}</>} · {(recipe.structure?.atoms ?? []).length} atoms ({rows.map((r) => `${r.count} ${r.label}`).join(", ")})
        {" "}· k-path {pathLabels(recipe)} ({pathPoints(recipe)} points). The atoms are in the Structure tab, moments in Magnetism, the path in Band path.
      </Text>
      <Select size="xs" label="Spin" data={SPIN_MODES} value={recipe.spin ?? "none"} allowDeselect={false}
        onChange={(v) => v && set(["spin"], v)} />
      <SimpleGrid cols={2} spacing="xs">
        <NumberInput size="xs" label="ecutwfc (Ry)" value={num(getIn(recipe, ["cutoffs", "ecutwfc"])) ?? ""} min={10} step={5}
          onChange={(v) => set(["cutoffs", "ecutwfc"], typeof v === "number" ? v : undefined)} />
        <NumberInput size="xs" label="ecutrho (Ry)" value={num(getIn(recipe, ["cutoffs", "ecutrho"])) ?? ""} min={40} step={40}
          onChange={(v) => set(["cutoffs", "ecutrho"], typeof v === "number" ? v : undefined)} />
      </SimpleGrid>
      <SimpleGrid cols={3} spacing="xs">
        <Select size="xs" label="Occupations" data={[{ value: "smearing", label: "Smearing" }, { value: "fixed", label: "Fixed" }]}
          value={kind} allowDeselect={false}
          onChange={(v) => v && set(["occupations"], v === "fixed" ? { kind: "fixed" } : { kind: "smearing", smearing: "mv", degauss: 0.01 })} />
        {kind === "smearing" && <>
          <Select size="xs" label="Smearing" data={SMEARINGS} value={occ.smearing ?? "mv"} allowDeselect={false}
            onChange={(v) => v && set(["occupations", "smearing"], v)} />
          <NumberInput size="xs" label="degauss (Ry)" value={num(occ.degauss) ?? ""} min={0.0001} step={0.005} decimalScale={4}
            onChange={(v) => set(["occupations", "degauss"], typeof v === "number" ? v : undefined)} />
        </>}
      </SimpleGrid>
      <Group gap="xs" align="flex-end">
        <Checkbox size="xs" label="SCF k-mesh from the spacing" checked={!kmesh}
          onChange={(e) => set(["scf", "kmesh"], e.currentTarget.checked ? undefined : [8, 8, 8])} />
      </Group>
      {kmesh && (
        <SimpleGrid cols={3} spacing="xs">
          {[0, 1, 2].map((i) => (
            <NumberInput key={i} size="xs" label={`k${i + 1}`} value={kmesh[i]} min={1} max={64}
              onChange={(v) => typeof v === "number" && set(["scf", "kmesh"], kmesh.map((x, j) => (j === i ? v : x)))} />
          ))}
        </SimpleGrid>
      )}
      <Group gap="xs" align="flex-end">
        <Checkbox size="xs" label="Density of states (an extra pw.x run on a dense k-mesh, then projwfc.x)" checked={!!recipe.dos}
          onChange={(e) => onChange(withDos(recipe, e.currentTarget.checked))} data-testid="dos-switch" />
        {recipe.dos && !getIn(recipe, ["dos", "kmesh"]) && (
          <NumberInput size="xs" w={150} label="DOS k-spacing (1/Å)" value={num(getIn(recipe, ["dos", "kspacing"])) ?? 0.1}
            min={0.02} max={0.5} step={0.02} decimalScale={3}
            onChange={(v) => typeof v === "number" && onChange(withDos(recipe, true, v))} />
        )}
      </Group>
      <SimpleGrid cols={2} spacing="xs">
        <NumberInput size="xs" label="Bands (nbnd)" description={nbnd == null || nbnd === "auto" ? "auto" : undefined}
          value={typeof nbnd === "number" ? nbnd : ""} min={1} onChange={(v) => set(["bands", "nbnd"], typeof v === "number" ? v : "auto")} />
        <TextInput size="xs" label="SCF conv_thr" value={String(getIn(recipe, ["electrons", "conv_thr"]) ?? "")}
          onChange={(e) => set(["electrons", "conv_thr"], num(e.currentTarget.value) ?? e.currentTarget.value)} />
      </SimpleGrid>
    </Stack>
  );
}

/** One row per species: the file, chosen from the library's options (recommended first). */
function PseudoTable({ recipe, onChange, projectId }: { recipe: Recipe; onChange: (r: Recipe) => void; projectId?: string }) {
  const rows = speciesRows(recipe);
  const elements = uniqueElements(rows);
  const spin = recipe.spin ?? "none";
  const library = recipeProtocol(recipe)?.library ?? null;
  const table = useQuery({ queryKey: ["qe-pseudos", elements.join(","), spin, library, projectId],
    queryFn: () => qePseudos(elements, spin, library, projectId), enabled: elements.length > 0, staleTime: 60_000 });
  const options = table.data?.options ?? {};
  const chosen = rows.map((r) => (options[r.element] ?? []).find((o) => o.file === r.pseudo)).filter(Boolean) as PseudoOption[];
  const cut = cutoffsFor(chosen);
  const useRecommended = () => {
    let r = recipe;
    for (const row of rows) {
      const best = (options[row.element] ?? [])[0];
      if (best) r = setPseudo(r, row.label, best.file);
    }
    const c = cutoffsFor(rows.map((row) => (options[row.element] ?? [])[0]).filter(Boolean) as PseudoOption[]);
    if (c) r = setIn(setIn(r, ["cutoffs", "ecutwfc"], c.ecutwfc), ["cutoffs", "ecutrho"], c.ecutrho);
    onChange(r);
  };
  return (
    <Stack gap={6} data-testid="qe-pseudos">
      <ErrorBox error={table.error} />
      <Table.ScrollContainer minWidth={620}>
      <Table withTableBorder verticalSpacing={4} fz="xs">
        <Table.Thead><Table.Tr><Table.Th>Species</Table.Th><Table.Th>Pseudopotential</Table.Th><Table.Th>What it is</Table.Th></Table.Tr></Table.Thead>
        <Table.Tbody>
          {rows.map((r) => {
            const opts = options[r.element] ?? [];
            const cur = opts.find((o) => o.file === r.pseudo);
            const data = opts.map((o) => ({ value: o.file, label: `${o.recommended ? "★ " : ""}${o.file}` }));
            if (r.pseudo && !cur) data.unshift({ value: r.pseudo, label: `${r.pseudo} (not in the library)` });
            return (
              <Table.Tr key={r.label}>
                <Table.Td><b>{r.label}</b> <Text span c="dimmed" size="xs">× {r.count}</Text></Table.Td>
                <Table.Td miw={240}>
                  <Select key={`${r.label}:${cur ? cur.file : "-"}:${opts.length}`}       // remount when the choices arrive
                    size="xs" data={data} value={r.pseudo || null} allowDeselect={false} searchable
                    placeholder={opts.length ? "Choose a file" : "no file for this element"} aria-label={`Pseudopotential of ${r.label}`}
                    onChange={(v) => v && onChange(setPseudo(recipe, r.label, v))} />
                </Table.Td>
                <Table.Td>
                  {cur ? (
                    <Text size="xs">{cur.source} · {cur.pseudo_type} · {cur.relativistic || "?"}-relativistic · {cur.functional}
                      {cur.z_valence != null ? ` · Z = ${cur.z_valence}` : ""} · {cur.orbitals || "no wavefunctions"}
                      {cur.ecutwfc ? ` · suggests ${cur.ecutwfc}/${cur.ecutrho} Ry` : ""}
                      {cur.notes.length > 0 && <Text span c="orange" size="xs"> · {cur.notes.join("; ")}</Text>}</Text>
                  ) : <Text size="xs" c="red">{opts.length ? "not in the library - choose one" : `no ${r.element} file in the library`}</Text>}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
      <Group gap="xs">
        <Button size="xs" variant="light" onClick={useRecommended} disabled={!table.data}>Use the recommended files and cutoffs</Button>
        {cut && <Button size="xs" variant="subtle" onClick={() => onChange(setIn(setIn(recipe, ["cutoffs", "ecutwfc"], cut.ecutwfc), ["cutoffs", "ecutrho"], cut.ecutrho))}>
          Cutoffs for these files: {cut.ecutwfc} / {cut.ecutrho} Ry</Button>}
      </Group>
    </Stack>
  );
}

function IssueList({ issues, color }: { issues: QeIssue[]; color: string }) {
  if (!issues.length) return null;
  return (
    <List size="xs" spacing={2} c={color}>
      {issues.map((i, k) => <List.Item key={k}><Code fz="xs">{i.where}</Code> {i.message}</List.Item>)}
    </List>
  );
}

/** The server's checks, the recipe as YAML (editable), and the three inputs. */
function CheckTabs({ recipe, check, nprocs, onYaml, projectId }: {
  recipe: Recipe; check: UseQueryResult<QeCheck>; nprocs: number; onYaml: (r: Recipe) => void; projectId?: string;
}) {
  const [tab, setTab] = useState<string | null>("checks");
  const c = check.data;
  const g = groupIssues(c?.issues ?? []);
  // the YAML text: follows the recipe, until the user edits it; edits are parsed by the server and then applied
  const [text, setText] = useState<string>("");
  const editing = useRef(false);
  useEffect(() => { if (!editing.current && c?.yaml) setText(c.yaml); }, [c?.yaml]);
  const [draft] = useDebouncedValue(text, 700);
  const [yamlError, setYamlError] = useState<string>("");
  useEffect(() => {
    if (!editing.current || !draft) return;
    qeParse(draft, "yaml").then((r) => {
      setYamlError("");
      if (JSON.stringify(r.recipe) !== JSON.stringify(recipe)) onYaml(r.recipe);
      editing.current = false;
    }).catch((e) => setYamlError(e instanceof Error ? e.message : String(e)));
  }, [draft]);
  const saveYaml = () => {
    const url = URL.createObjectURL(new Blob([text], { type: "text/yaml" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${(recipe.name || "recipe").replace(/[^A-Za-z0-9_.-]+/g, "_")}.yaml`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };
  const time = roughTime(c?.estimate ?? null, nprocs);
  return (
    <Tabs value={tab} onChange={setTab} keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="checks" rightSection={c && <Badge size="xs" color={g.error.length ? "red" : g.warning.length ? "orange" : "teal"}>
          {g.error.length ? `${g.error.length} error${g.error.length > 1 ? "s" : ""}` : g.warning.length ? g.warning.length : "ok"}</Badge>}>Checks</Tabs.Tab>
        <Tabs.Tab value="structure">Structure</Tabs.Tab>
        <Tabs.Tab value="magnetism">Magnetism</Tabs.Tab>
        <Tabs.Tab value="hubbard">DFT+U</Tabs.Tab>
        <Tabs.Tab value="path">Band path</Tabs.Tab>
        <Tabs.Tab value="defects" rightSection={recipe.defect ? <Badge size="xs" color="grape">{String(recipe.defect.label ?? "")}</Badge>
          : recipe.relax ? <Badge size="xs" color="gray">relax</Badge> : undefined}>Defects</Tabs.Tab>
        <Tabs.Tab value="yaml">Recipe (YAML)</Tabs.Tab>
        <Tabs.Tab value="inputs" disabled={!c?.inputs}>Inputs</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="checks" pt="xs">
        <Stack gap="xs" data-testid="qe-checks">
          <ErrorBox error={check.error} />
          {c && g.error.length > 0 && <Alert color="red" p="xs" title="Errors - the recipe cannot run"><IssueList issues={g.error} color="red.9" /></Alert>}
          {c && g.warning.length > 0 && <Alert color="orange" p="xs" title="Warnings - it runs, but check these"><IssueList issues={g.warning} color="orange.9" /></Alert>}
          {c?.ok && c.estimate && (
            <Text size="xs">Size: {fmtEstimate(c.estimate)}{time ? ` · run time ${time} on ${nprocs} process${nprocs > 1 ? "es" : ""}` : ""}.
              {" "}The Slater-Koster fit gets {c.expected_outputs?.join(", ")}.</Text>
          )}
          {c && g.note.length > 0 && (
            <Accordion variant="default" chevronPosition="left">
              <Accordion.Item value="notes">
                <Accordion.Control><Text size="xs">Notes ({g.note.length})</Text></Accordion.Control>
                <Accordion.Panel><IssueList issues={g.note} color="dimmed" /></Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}
          {check.isFetching && <Text size="xs" c="dimmed">checking…</Text>}
        </Stack>
      </Tabs.Panel>
      <Tabs.Panel value="structure" pt="xs">
        <Stack gap="sm">
          <RecipeStructure recipe={recipe} />
          <AtomsTable recipe={recipe} onChange={onYaml} />
        </Stack>
      </Tabs.Panel>
      <Tabs.Panel value="magnetism" pt="xs"><MagnetismPanel recipe={recipe} onChange={onYaml} /></Tabs.Panel>
      <Tabs.Panel value="hubbard" pt="xs"><HubbardPanel recipe={recipe} onChange={onYaml} /></Tabs.Panel>
      <Tabs.Panel value="path" pt="xs"><PathEditor recipe={recipe} onChange={onYaml} /></Tabs.Panel>
      <Tabs.Panel value="defects" pt="xs"><DefectsPanel recipe={recipe} onChange={onYaml} projectId={projectId} /></Tabs.Panel>
      <Tabs.Panel value="yaml" pt="xs">
        <Stack gap={4}>
          <Textarea value={text} autosize minRows={12} maxRows={28} spellCheck={false} aria-label="Recipe (YAML)"
            styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)", fontSize: 12 } }}
            onChange={(e) => { editing.current = true; setText(e.currentTarget.value); }} />
          {yamlError && <Text size="xs" c="red">{yamlError}</Text>}
          <Group justify="space-between">
            <Text size="xs" c="dimmed">Edit anything here (structure, path, expert settings); the form and the checks follow.</Text>
            <Button size="xs" variant="default" onClick={saveYaml}>Download recipe</Button>
          </Group>
        </Stack>
      </Tabs.Panel>
      <Tabs.Panel value="inputs" pt="xs">
        <Stack gap="xs">
          {Object.entries(c?.inputs ?? {}).map(([name, body]) => (
            <Stack key={name} gap={2}>
              <Text size="xs" fw={600}>{name}</Text>
              <ScrollArea.Autosize mah={260}><Code block fz="xs">{body}</Code></ScrollArea.Autosize>
            </Stack>
          ))}
          <Text size="xs" c="dimmed">prefix, outdir and pseudo_dir are set by the server when it runs.</Text>
        </Stack>
      </Tabs.Panel>
    </Tabs>
  );
}

/** What the run reported, and its files. */
export function QeSummaryCard({ summary, files, seed }: { summary: QeSummary; files: Record<string, string>; seed: string }) {
  const getZip = useMutation({ mutationFn: () => download(`/files/${files["qe_run.zip"]}/download`, `${seed}_qe_run.zip`),
    onError: (e) => notifyError(e) });
  if (!summary) return null;
  const steps = summary.steps ?? {};
  const fmt = (s?: number) => (s == null ? "?" : s < 90 ? `${Math.round(s)} s` : `${(s / 60).toFixed(1)} min`);
  return (
    <Card withBorder p="sm" data-testid="qe-summary">
      <Stack gap={4}>
        <Text size="sm" fw={600}>Quantum ESPRESSO {summary.qe_version ?? ""}{summary.recipe ? ` · ${summary.recipe}` : ""}</Text>
        <Text size="xs">{summary.kpoints_path} k-points × {summary.bands} bands · {summary.electrons} electrons
          {summary.spinorbit ? " · spin-orbit" : summary.lsda ? " · spin-polarised" : ""}</Text>
        {summary.fermi_energy_eV != null && <Text size="xs">{summary.fermi_source ?? "Fermi energy"}: {summary.fermi_energy_eV.toFixed(4)} eV
          {summary.gap_eV ? ` · ${summary.gap_direct ? "direct " : summary.gap_direct === false ? "indirect " : ""}gap along the path ${summary.gap_eV.toFixed(4)} eV`
            : summary.gap_eV === 0 ? " · no gap along the path" : ""}</Text>}
        {summary.total_magnetization != null && <Text size="xs" data-testid="qe-magnetization">Magnetisation: {summary.total_magnetization.toFixed(2)} μB/cell
          {summary.absolute_magnetization != null ? ` (absolute ${summary.absolute_magnetization.toFixed(2)})` : ""}
          {summary.site_moments?.length ? ` · per site ${summary.site_moments.map((m) => m.toFixed(2)).join(", ")}` : ""}</Text>}
        {summary.relax && <Text size="xs" data-testid="qe-relax">Relaxation: {summary.relax.bfgs_steps} steps
          {summary.relax.converged ? "" : " (not converged)"}, energy {summary.relax.energy_change_eV >= 0 ? "+" : ""}{summary.relax.energy_change_eV.toFixed(3)} eV,
          {" "}largest move {summary.relax.max_displacement_A.toFixed(3)} Å{summary.relax.displacements[0] ? ` (atom ${summary.relax.displacements[0].atom})` : ""}</Text>}
        <Text size="xs" c="dimmed">{fmt(summary.seconds)} on {summary.nprocs} process{summary.nprocs > 1 ? "es" : ""}: {steps.relax ? `relaxation ${fmt(steps.relax.seconds)}, ` : ""}SCF {fmt(steps.scf?.seconds)}
          {steps.scf?.iterations ? ` (${steps.scf.iterations} iterations)` : ""}, bands {fmt(steps.bands?.seconds)}, projections {fmt(steps.projwfc?.seconds)}
          {steps.dos ? `, density of states ${fmt((steps.dos?.seconds ?? 0) + (steps.dos_projwfc?.seconds ?? 0))}` : ""}</Text>
        {!summary.dos && (summary.pdos_files ?? 0) > 0 && <Text size="xs" c="dimmed">The zip's pdos/ files are projections along the
          k-path, not a density of states: switch "Density of states" on in the settings for one.</Text>}
        {(summary.warnings ?? []).map((w) => <Text key={w} size="xs" c="orange">{w}</Text>)}
        {files["qe_run.zip"] && <Group><Button size="xs" variant="default" loading={getZip.isPending} onClick={() => getZip.mutate()}>
          Download the run (inputs, outputs, recipe, provenance)</Button></Group>}
      </Stack>
    </Card>
  );
}
